import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTracking } from '../hooks/useTracking';
import { trackingService } from '../services/trackingService';
import { DataAndReporting } from '../types';

interface TrackingContextType {
  // Current tracking state
  currentLessonId: string | null;
  currentStudentId: string | null;
  currentSessionId: string | null;
  isTrackingEnabled: boolean;

  // Tracking functions
  startTracking: (lessonId: string, studentId: string, dataAndReporting?: DataAndReporting) => void;
  stopTracking: () => void;
  trackEvent: (eventType: string, data?: Record<string, any>) => Promise<void>;
  trackStepComplete: (stepId: string, timeSpent: number, completed?: boolean) => Promise<void>;
  trackAssessmentComplete: (assessmentId: string, score: number, timeSpent: number) => Promise<void>;
  trackResourceInteraction: (resourceId: string, actionType: string, duration?: number, status?: boolean, score?: number) => Promise<void>;
  trackQuizResponse: (questionId: string, answer: any, correct: boolean, timeSpent: number) => Promise<void>;
  trackEngagementCheck: (checkType: string, response: any) => Promise<void>;
  trackExitTicket: (response: string, understandingLevel: number, timeSpent: number) => Promise<void>;

  // Data access
  getStudentProgress: () => Promise<any>;
  getAnalytics: () => Promise<any>;
  getLessonEvents: () => Promise<any[]>;
  getLessonSummary: () => Promise<any>;

  // Export functions
  exportData: (format?: 'csv' | 'json' | 'pdf') => Promise<string>;
  clearLessonData: (lessonId: string) => Promise<void>;

  // Real-time tracking state
  trackingStats: {
    totalEvents: number;
    totalTime: number;
    lastActivity: Date | null;
  };
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

interface TrackingProviderProps {
  children: ReactNode;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null);
  const [currentStudentId, setCurrentStudentId] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentDataAndReporting, setCurrentDataAndReporting] = useState<DataAndReporting | undefined>();
  const [trackingStats, setTrackingStats] = useState({
    totalEvents: 0,
    totalTime: 0,
    lastActivity: null as Date | null
  });

  // Use refs to store current values for stable function references
  const currentLessonIdRef = useRef<string | null>(null);
  const currentStudentIdRef = useRef<string | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const currentDataAndReportingRef = useRef<DataAndReporting | undefined>(undefined);

  // Update refs when state changes
  useEffect(() => {
    currentLessonIdRef.current = currentLessonId;
  }, [currentLessonId]);

  useEffect(() => {
    currentStudentIdRef.current = currentStudentId;
  }, [currentStudentId]);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  useEffect(() => {
    currentDataAndReportingRef.current = currentDataAndReporting;
  }, [currentDataAndReporting]);

  // Use the tracking hook with current state
  const trackingHook = useTracking({
    lessonId: currentLessonId || '',
    studentId: currentStudentId || '',
    dataAndReporting: currentDataAndReporting,
    sessionId: currentSessionId || undefined
  });

  // Update tracking stats when events change
  useEffect(() => {
    const updateStats = async () => {
      if (currentLessonId) {
        try {
          const [events, summary] = await Promise.all([
            trackingService.getLessonEvents(currentLessonId),
            trackingService.getLessonSummary(currentLessonId)
          ]);
          
          const lastEvent = events.length > 0 ? events[events.length - 1] : null;
          const lastActivity = lastEvent 
            ? (lastEvent.timestamp instanceof Date 
                ? lastEvent.timestamp 
                : (lastEvent.timestamp as any).toDate())
            : null;

          setTrackingStats({
            totalEvents: events.length,
            totalTime: summary.averageTime,
            lastActivity
          });
        } catch (error) {
          console.error('Error updating tracking stats:', error);
        }
      }
    };

    updateStats();
  }, [currentLessonId]);

  // Start tracking for a lesson and student
  const startTracking = useCallback(async (
    lessonId: string,
    studentId: string,
    dataAndReporting?: DataAndReporting
  ) => {
    setCurrentLessonId(lessonId);
    setCurrentStudentId(studentId);
    setCurrentSessionId(`session_${Date.now()}`);
    setCurrentDataAndReporting(dataAndReporting);

    // Track session start
    if (dataAndReporting?.analytics_enabled) {
      try {
        const sessionId = `session_${Date.now()}`;
        await trackingService.trackEvent(lessonId, studentId, 'session_start', {
          lessonId,
          studentId,
          timestamp: new Date().toISOString()
        }, sessionId);
      } catch (error) {
        console.error('Error tracking session start:', error);
      }
    } else {
      console.log('Tracking is not enabled', lessonId, studentId, 'session_start');
    }
  }, []);

  // Stop tracking
  const stopTracking = useCallback(async () => {
    // Use refs to get current values without dependencies
    const lessonId = currentLessonIdRef.current;
    const studentId = currentStudentIdRef.current;
    const sessionId = currentSessionIdRef.current;
    const dataAndReporting = currentDataAndReportingRef.current;
    console.log('stopTracking', lessonId, studentId, sessionId, dataAndReporting);

    if (lessonId && studentId && dataAndReporting?.analytics_enabled) {
      // Track session end
      try {
        await trackingService.trackEvent(lessonId, studentId, 'session_end', {
          lessonId,
          studentId,
          timestamp: new Date().toISOString()
        }, sessionId || `session_${Date.now()}`);
      } catch (error) {
        console.error('Error tracking session end:', error);
      }
    }

    setCurrentLessonId(null);
    setCurrentStudentId(null);
    setCurrentSessionId(null);
    setCurrentDataAndReporting(undefined);
  }, []);

  // Wrapper functions that check if tracking is active
  const trackEvent = useCallback(async (eventType: string, data?: Record<string, any>): Promise<void> => {
    console.log('trackEvent', eventType, data);
    if (trackingHook.isTrackingEnabled && currentLessonId) {
      await trackingHook.trackEvent(eventType, data);
    }
  }, [trackingHook, currentLessonId]);

  const trackStepComplete = useCallback(async (stepId: string, timeSpent: number, completed?: boolean): Promise<void> => {
    console.log('trackStepComplete', stepId, timeSpent, completed);
    if (trackingHook.isTrackingEnabled && currentLessonId) {
      await trackingHook.trackStepComplete(stepId, timeSpent, completed);
    }
  }, [trackingHook, currentLessonId]);

  const trackAssessmentComplete = useCallback(async (assessmentId: string, score: number, timeSpent: number): Promise<void> => {
    console.log('trackAssessmentComplete', assessmentId, score, timeSpent);
    if (trackingHook.isTrackingEnabled && currentLessonId && currentStudentId) {
      await trackingHook.trackAssessmentComplete(assessmentId, score, timeSpent);
    }
  }, [trackingHook, currentLessonId, currentStudentId]);

  const trackResourceInteraction = useCallback(async (resourceId: string, actionType: string, duration?: number, status?: boolean, score?: number): Promise<void> => {
    console.log('trackResourceInteraction', resourceId, actionType, duration, status, score);
    if (trackingHook.isTrackingEnabled && currentLessonId && currentStudentId) {
      await trackingHook.trackResourceInteraction(resourceId, actionType, duration, status, score);
    }
  }, [trackingHook, currentLessonId, currentStudentId]);

  const trackQuizResponse = useCallback(async (questionId: string, answer: any, correct: boolean, timeSpent: number): Promise<void> => {
    console.log('trackQuizResponse', questionId, answer, correct, timeSpent);
    if (trackingHook.isTrackingEnabled && currentLessonId && currentStudentId) {
      await trackingHook.trackQuizResponse(questionId, answer, correct, timeSpent);
    }
  }, [trackingHook, currentLessonId, currentStudentId]);

  const trackEngagementCheck = useCallback(async (checkType: string, response: any): Promise<void> => {
    console.log('trackEngagementCheck', checkType, response);
    if (trackingHook.isTrackingEnabled && currentLessonId && currentStudentId) {
      await trackingHook.trackEngagementCheck(checkType, response);
    }
  }, [trackingHook, currentLessonId, currentStudentId]);

  const trackExitTicket = useCallback(async (response: string, understandingLevel: number, timeSpent: number): Promise<void> => {
    console.log('trackExitTicket', response, understandingLevel, timeSpent);
    if (trackingHook.isTrackingEnabled && currentLessonId && currentStudentId) {
      await trackingHook.trackExitTicket(response, understandingLevel, timeSpent);
    }
  }, [trackingHook, currentLessonId, currentStudentId]);

  // Data access functions
  const getStudentProgress = useCallback(async (): Promise<any> => {
    if (currentLessonId && currentStudentId) {
      return await trackingHook.getStudentProgress();
    }
    return null;
  }, [trackingHook, currentLessonId, currentStudentId]);

  const getAnalytics = useCallback(async (): Promise<any> => {
    if (currentLessonId) {
      return await trackingHook.getAnalytics();
    }
    return null;
  }, [trackingHook, currentLessonId]);

  const getLessonEvents = useCallback(async (): Promise<any[]> => {
    if (currentLessonId) {
      return await trackingHook.getLessonEvents();
    }
    return [];
  }, [trackingHook, currentLessonId]);

  const getLessonSummary = useCallback(async (): Promise<any> => {
    if (currentLessonId) {
      return await trackingHook.getLessonSummary();
    }
    return {
      totalStudents: 0,
      averageTime: 0,
      averageScore: 0,
      completionRate: 0,
      engagementRate: 0
    };
  }, [trackingHook, currentLessonId]);

  // Export and utility functions
  const exportData = useCallback(async (format?: 'csv' | 'json' | 'pdf'): Promise<string> => {
    if (currentLessonId) {
      return await trackingHook.exportData(format);
    }
    return '';
  }, [trackingHook, currentLessonId]);

  const clearLessonData = useCallback(async (lessonId: string): Promise<void> => {
    await trackingService.clearLessonData(lessonId);
  }, []);

  const contextValue: TrackingContextType = useMemo(() => ({
    // Current tracking state
    currentLessonId,
    currentStudentId,
    currentSessionId,
    isTrackingEnabled: trackingHook.isTrackingEnabled,

    // Tracking functions
    startTracking,
    stopTracking,
    trackEvent,
    trackStepComplete,
    trackAssessmentComplete,
    trackResourceInteraction,
    trackQuizResponse,
    trackEngagementCheck,
    trackExitTicket,

    // Data access
    getStudentProgress,
    getAnalytics,
    getLessonEvents,
    getLessonSummary,

    // Export functions
    exportData,
    clearLessonData,

    // Real-time tracking state
    trackingStats
  }), [
    currentLessonId,
    currentStudentId,
    currentSessionId,
    trackingHook.isTrackingEnabled,
    startTracking,
    stopTracking,
    trackEvent,
    trackStepComplete,
    trackAssessmentComplete,
    trackResourceInteraction,
    trackQuizResponse,
    trackEngagementCheck,
    trackExitTicket,
    getStudentProgress,
    getAnalytics,
    getLessonEvents,
    getLessonSummary,
    exportData,
    clearLessonData,
    trackingStats
  ]);

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
    </TrackingContext.Provider>
  );
};

// Custom hook to use tracking context
export const useTrackingContext = (): TrackingContextType => {
  const context = useContext(TrackingContext);
  if (context === undefined) {
    throw new Error('useTrackingContext must be used within a TrackingProvider');
  }
  return context;
};
